/**
 * Relation-aware BFF invalidation for yard WebSocket events.
 * See docs/yard-websocket-entity-mapping.md for the full entity mapping.
 *
 * @param {string} eventKey - e.g. 'update-AssetContainer', 'create-CustomFormData'
 * @param {object} payload - WebSocket payload (entity snapshot)
 * @param {object} context - { selectedWarehouseId?, selectedSpotId?, allowedOrgId?, getGridSpotIds?(), getSelectedSpotDetailPayload?() }
 * @returns {{ invalidateGrid: boolean, invalidateList: boolean, invalidateSpotDetail: boolean, spotIdForDetail?: string | null }}
 */
export function computeYardBffInvalidation(eventKey, payload, context = {}) {
  const result = {
    invalidateGrid: false,
    invalidateList: false,
    invalidateSpotDetail: false,
    spotIdForDetail: null
  };

  if (!payload || typeof payload !== 'object') {
    return result;
  }

  const {
    selectedWarehouseId,
    selectedSpotId,
    allowedOrgId,
    getGridSpotIds,
    getSelectedSpotDetailPayload
  } = context;
  const gridSpotIds = typeof getGridSpotIds === 'function' ? getGridSpotIds() : [];
  const selectedDetail =
    typeof getSelectedSpotDetailPayload === 'function' ? getSelectedSpotDetailPayload() : null;

  const entity = parseEntityFromEventKey(eventKey);
  if (!entity) {
    return result;
  }

  if (allowedOrgId != null && payload.orgId != null && payload.orgId !== allowedOrgId) {
    return result;
  }

  const warehouseMatch =
    payload.warehouseId != null &&
    selectedWarehouseId != null &&
    payload.warehouseId === selectedWarehouseId;
  const hasWarehouseId = payload.warehouseId != null;
  const spotId = resolveSpotIdForEntity(entity, payload);

  const spotIdOnGrid = spotId != null && Array.isArray(gridSpotIds) && gridSpotIds.includes(spotId);

  const skipForWarehouseMismatch = hasWarehouseId && !warehouseMatch;
  const applyGridListAndSpotDetail = shouldInvalidateSpotDetail => {
    result.invalidateGrid = true;
    result.invalidateList = true;
    if (selectedSpotId && shouldInvalidateSpotDetail) {
      result.invalidateSpotDetail = true;
      result.spotIdForDetail = selectedSpotId;
    }
  };

  switch (entity) {
    case 'Spot':
      if (skipForWarehouseMismatch) {
        return result;
      }
      applyGridListAndSpotDetail(payload.id === selectedSpotId);
      return result;

    case 'SpotAssignment':
    case 'SpotReserve':
    case 'SpotEvent':
      if (skipForWarehouseMismatch) {
        return result;
      }
      applyGridListAndSpotDetail(spotId === selectedSpotId);
      return result;

    case 'AssetVisitEvent':
      if (!spotId || !spotIdOnGrid) {
        return result;
      }
      applyGridListAndSpotDetail(spotId === selectedSpotId);
      return result;

    case 'SpotArea':
      if (!warehouseMatch) {
        return result;
      }
      applyGridListAndSpotDetail(selectedDetail?.spot?.spotAreaId === payload.id);
      return result;

    case 'AssetContainer':
    case 'AssetVisit':
      if (!warehouseMatch) {
        return result;
      }
      applyGridListAndSpotDetail(spotId === selectedSpotId);
      return result;

    case 'AssetContainerEvent': {
      // No warehouseId in payload — we cannot know which warehouse. Only invalidate when the
      // container is on the selected spot (then we know it's our view). Do NOT invalidate on
      // selectedWarehouseId alone or we would react to every org's container events (all warehouses).
      const containerId = payload.assetContainerId ?? payload.asset_container_id;
      if (selectedSpotId && containerId && selectedDetail?.spot) {
        const hasContainer = selectedDetail.spot.spotAssignments?.some(
          sa => (sa.assetContainer?.id ?? sa.assetContainerId) === containerId
        );
        if (hasContainer) {
          result.invalidateSpotDetail = true;
          result.spotIdForDetail = selectedSpotId;
          result.invalidateGrid = true;
          result.invalidateList = true;
        }
      }
      return result;
    }

    case 'Appointment': {
      if (!warehouseMatch) {
        return result;
      }
      const appointmentId = payload.id;
      let appointmentAffectsSelectedSpot = false;
      if (selectedSpotId && appointmentId && selectedDetail?.spot) {
        const visit = selectedDetail.spot.spotAssignments?.[0]?.assetVisit;
        const visitAppointmentId = visit?.appointmentId ?? visit?.appointment?.id;
        const container = selectedDetail.spot.spotAssignments?.[0]?.assetContainer;
        const pickupId = container?.pickupAppointment?.id ?? container?.pickupAppointmentId;
        const dropId = container?.dropAppointment?.id ?? container?.dropAppointmentId;
        appointmentAffectsSelectedSpot =
          visitAppointmentId === appointmentId ||
          pickupId === appointmentId ||
          dropId === appointmentId;
      }
      applyGridListAndSpotDetail(appointmentAffectsSelectedSpot);
      return result;
    }

    case 'CustomFormData': {
      const entityName = (payload.entityName ?? payload.entity_name ?? '').toLowerCase();
      const objectId = payload.objectId ?? payload.object_id ?? null;
      if (entityName !== 'assetcontainer' && entityName !== 'assetvisit') {
        return result;
      }
      if (skipForWarehouseMismatch) {
        return result;
      }
      let customFormAffectsSelectedSpot = false;
      if (selectedSpotId && objectId && selectedDetail?.spot) {
        const assignment = selectedDetail.spot.spotAssignments?.[0];
        const visitId = assignment?.assetVisit?.id ?? assignment?.assetVisitId;
        const containerId = assignment?.assetContainer?.id ?? assignment?.assetContainerId;
        customFormAffectsSelectedSpot =
          (entityName === 'assetvisit' && visitId === objectId) ||
          (entityName === 'assetcontainer' && containerId === objectId);
      }
      applyGridListAndSpotDetail(customFormAffectsSelectedSpot);
      return result;
    }

    default:
      return result;
  }
}

/** Entities that trigger BFF WebSocket events. Keep in sync with backend event keys. */
export const BFF_WS_ENTITIES = [
  'AssetVisit',
  'AssetContainer',
  'AssetVisitEvent',
  'AssetContainerEvent',
  'SpotAssignment',
  'Spot',
  'SpotEvent',
  'SpotReserve',
  'SpotArea',
  'Appointment',
  'CustomFormData'
];

/** Entities we track for "ignore own WebSocket echo" after a local mutation. */
export const LOCAL_MUTATION_ENTITIES = ['Spot', 'AssetContainer', 'AssetVisit'];

/**
 * @param {string} eventKey - e.g. 'update-Spot'
 * @returns {string | null} - Entity name or null
 */
export function parseEntityFromEventKey(eventKey) {
  if (typeof eventKey !== 'string') {
    return null;
  }
  for (const entity of BFF_WS_ENTITIES) {
    if (eventKey.endsWith(`-${entity}`)) {
      return entity;
    }
  }
  return null;
}

function resolveSpotIdForEntity(entity, payload) {
  if (!payload) {
    return null;
  }
  switch (entity) {
    case 'Spot':
      return payload.id ?? null;
    case 'SpotAssignment':
    case 'SpotReserve':
    case 'SpotEvent':
    case 'AssetVisitEvent':
      return payload.spotId ?? payload.spot_id ?? null;
    case 'AssetContainer':
    case 'AssetVisit':
      return (
        payload.spotAssignments?.[0]?.spotId ??
        payload.spotAssignments?.[0]?.spot_id ??
        payload.spotId ??
        payload.spot_id ??
        payload.spot?.id ??
        null
      );
    case 'SpotArea':
    case 'AssetContainerEvent':
    case 'Appointment':
    case 'CustomFormData':
    default:
      return null;
  }
}
